import { Committee, CommitteeMember, MeetingFrequency, TermsOfReference } from "@/types/governance";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface TorFormProps {
  committee: Committee;
  tor: TermsOfReference;
  members: CommitteeMember[];
  ownerName: string;
  onChange: (patch: Partial<TermsOfReference>) => void;
  onSaveDraft: () => void;
  onSubmitForApproval: () => void;
  onPreview: () => void;
}

const frequencyLabels: Record<MeetingFrequency, string> = {
  weekly: "Weekly",
  bi_weekly: "Bi-weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

export function TorForm({
  committee,
  tor,
  members,
  ownerName,
  onChange,
  onSaveDraft,
  onSubmitForApproval,
  onPreview,
}: TorFormProps) {
  const selectedMembers = members.filter((member) => tor.memberIds.includes(member.id));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Committee Name</Label>
          <Input value={committee.name} disabled />
        </div>
        <div>
          <Label>Committee Owner</Label>
          <Input value={ownerName} disabled />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Assign Chairperson</Label>
          <Select
            value={tor.chairpersonId ?? "none"}
            onValueChange={(value) => onChange({ chairpersonId: value === "none" ? null : value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select chairperson" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not Assigned</SelectItem>
              {members.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  {member.name} ({member.designation})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="minimum-members">Minimum Members Required</Label>
          <Input
            id="minimum-members"
            type="number"
            value={tor.minimumMembersRequired}
            onChange={(event) => onChange({ minimumMembersRequired: Number(event.target.value || 0) })}
            min={1}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label>Meeting Frequency</Label>
          <Select
            value={tor.meetingFrequency}
            onValueChange={(value: MeetingFrequency) => onChange({ meetingFrequency: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(frequencyLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="tor-version">ToR Version</Label>
          <Input
            id="tor-version"
            value={tor.version}
            onChange={(event) => onChange({ version: event.target.value })}
            placeholder="v1.0"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="reporting-workflow">Reporting Workflow</Label>
          <Textarea
            id="reporting-workflow"
            value={tor.reportingWorkflow}
            onChange={(event) => onChange({ reportingWorkflow: event.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="approval-workflow">Approval Workflow</Label>
          <Textarea
            id="approval-workflow"
            value={tor.approvalWorkflow}
            onChange={(event) => onChange({ approvalWorkflow: event.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Assigned Members (view-only in this MVP)</Label>
        <div className="flex flex-wrap gap-2 rounded-md border bg-background p-3">
          {selectedMembers.map((member) => (
            <Badge key={member.id} variant="secondary">
              {member.name}
            </Badge>
          ))}
          {selectedMembers.length === 0 && (
            <span className="text-xs text-muted-foreground">No members assigned yet.</span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Purpose of the Committee</Label>
        <ReactQuill value={tor.purpose} onChange={(value) => onChange({ purpose: value })} theme="snow" />
      </div>

      <div className="space-y-2">
        <Label>Roles and Responsibilities</Label>
        <ReactQuill
          value={tor.rolesAndResponsibilities}
          onChange={(value) => onChange({ rolesAndResponsibilities: value })}
          theme="snow"
        />
      </div>

      <div className="space-y-2">
        <Label>Agenda Setup</Label>
        <ReactQuill value={tor.agendaContent} onChange={(value) => onChange({ agendaContent: value })} theme="snow" />
      </div>

      <div className="flex flex-wrap gap-3">
        <Button variant="outline" onClick={onSaveDraft}>
          Save Draft
        </Button>
        <Button onClick={onSubmitForApproval}>Submit for Approval</Button>
        <Button variant="secondary" onClick={onPreview}>
          Preview PDF
        </Button>
      </div>
    </div>
  );
}
